import * as React from "react";
import { useCallback, useEffect, useState } from "react";
import { Copy, Loader2, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { STRATEGY_PINE_SECTION_LABEL_CLASS } from "@/components/Strategies/StrategyPineSectionCard";
import { Card, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import type { WorkspaceSummary } from "../../../types/workspace";
import { statisticsSidebarSurfaceCardClassName } from "../Statistics/components/statisticsShadcnCardShell";
import {
  WorkspaceCanvasPreview,
  type WorkspaceCanvasPreviewGraph,
} from "./WorkspaceCanvasPreview";
import { WORKSPACE_HUB_ENTITY_TITLE_CLASS } from "./WorkspaceHubLinkCell";

const workspaceCardDialogClassName =
  "max-w-[calc(100%-2rem)] font-[family-name:var(--font-primary)] sm:max-w-md";

const MAX_NAME_LENGTH = 120;

function formatUpdatedLabel(updatedAt?: string): string {
  if (!updatedAt?.trim()) return "—";
  const date = new Date(updatedAt);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export type WorkspaceCardProps = {
  workspace: WorkspaceSummary;
  onOpen: (workspace: WorkspaceSummary) => void;
  onRename?: (workspace: WorkspaceSummary, name: string) => void | Promise<void>;
  onDelete?: (workspace: WorkspaceSummary) => void | Promise<void>;
  mutating?: boolean;
  /** Real saved graph for the card thumbnail; omit while loading. */
  previewGraph?: WorkspaceCanvasPreviewGraph | null;
  previewLoading?: boolean;
};

export function WorkspaceCard({
  workspace,
  onOpen,
  onRename,
  onDelete,
  mutating = false,
  previewGraph = null,
  previewLoading = false,
}: WorkspaceCardProps): React.ReactElement {
  const [renameOpen, setRenameOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [name, setName] = useState(workspace.name ?? "");
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [pending, setPending] = useState(false);

  const workspaceDisplayName = workspace.name?.trim() || "Untitled workspace";
  const deleteConfirmMatches = deleteConfirmText.trim() === workspaceDisplayName;

  useEffect(() => {
    if (renameOpen) setName(workspace.name ?? "");
  }, [renameOpen, workspace.name]);

  useEffect(() => {
    if (deleteOpen) setDeleteConfirmText("");
  }, [deleteOpen]);

  const nodeLabel =
    typeof workspace.nodeCount === "number"
      ? `${workspace.nodeCount} node${workspace.nodeCount === 1 ? "" : "s"}`
      : null;

  const submitRename = useCallback(async () => {
    const trimmed = name.trim();
    if (!trimmed || !onRename) return;
    setPending(true);
    try {
      await onRename(workspace, trimmed);
      setRenameOpen(false);
    } finally {
      setPending(false);
    }
  }, [name, onRename, workspace]);

  const submitDelete = useCallback(async () => {
    if (!onDelete || !deleteConfirmMatches) return;
    setPending(true);
    try {
      await onDelete(workspace);
      setDeleteOpen(false);
      setDeleteConfirmText("");
    } finally {
      setPending(false);
    }
  }, [deleteConfirmMatches, onDelete, workspace]);

  const copyWorkspaceName = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(workspaceDisplayName);
      toast.success("Workspace name copied");
    } catch {
      toast.error("Could not copy workspace name");
    }
  }, [workspaceDisplayName]);

  const busy = mutating || pending;

  return (
    <>
      <Card
        data-workspace-card=""
        size="sm"
        className={cn(
          "flex h-full flex-col overflow-hidden !rounded-[6px] border border-solid border-border bg-card p-0 py-0 text-card-foreground shadow-none !ring-0 transition-colors",
          "gap-0 data-[size=sm]:gap-0 data-[size=sm]:py-0",
          "hover:border-[#38BBD1]/50",
          statisticsSidebarSurfaceCardClassName
        )}
      >
        <button
          type="button"
          className="block w-full cursor-pointer border-0 bg-transparent p-0 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-0"
          disabled={busy}
          onClick={() => onOpen(workspace)}
          aria-label={`Open canvas for ${workspace.name?.trim() || "Untitled workspace"}`}
        >
          <WorkspaceCanvasPreview graph={previewGraph} loading={previewLoading} />
        </button>
        <CardHeader className="gap-1 px-4 pb-2 pt-3">
          <CardTitle
            data-workspace-hub-entity-title=""
            className={cn(
              WORKSPACE_HUB_ENTITY_TITLE_CLASS,
              "line-clamp-2 whitespace-normal"
            )}
          >
            {workspace.name?.trim() || "Untitled workspace"}
          </CardTitle>
          <p
            data-workspace-card-meta=""
            className="font-[family-name:var(--font-primary)] text-xs text-gray-500 dark:text-sidebar-foreground"
          >
            Updated {formatUpdatedLabel(workspace.updatedAt)}
            {nodeLabel ? ` · ${nodeLabel}` : null}
          </p>
        </CardHeader>
        <CardFooter className="flex flex-col gap-2 border-0 bg-transparent px-4 pb-4 pt-0">
          <Button
            type="button"
            variant="gradient"
            size="sm"
            data-font="primary"
            className="w-full"
            disabled={busy}
            onClick={() => onOpen(workspace)}
          >
            <span className="normal-case tracking-normal">Open canvas</span>
          </Button>
          {onRename || onDelete ? (
            <div className="flex w-full gap-2">
              {onRename ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline-gray"
                  data-font="primary"
                  className="flex-1"
                  disabled={busy}
                  onClick={() => setRenameOpen(true)}
                >
                  <Pencil data-icon="inline-start" className="size-3.5" aria-hidden />
                  <span className="normal-case tracking-normal">Rename</span>
                </Button>
              ) : null}
              {onDelete ? (
                <Button
                  type="button"
                  size="sm"
                  variant="outline-red"
                  data-font="primary"
                  className="flex-1"
                  disabled={busy}
                  onClick={() => setDeleteOpen(true)}
                >
                  <Trash2 data-icon="inline-start" className="size-3.5" aria-hidden />
                  <span className="normal-case tracking-normal">Delete</span>
                </Button>
              ) : null}
            </div>
          ) : null}
        </CardFooter>
      </Card>

      <Dialog open={renameOpen} onOpenChange={setRenameOpen}>
        <DialogContent className={workspaceCardDialogClassName}>
          <DialogHeader>
            <DialogTitle className="text-[color:var(--brand-tropical-surf)]">
              Rename workspace
            </DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
              Update the display name for this workspace.
            </DialogDescription>
          </DialogHeader>
          <Field>
            <FieldLabel className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Name</FieldLabel>
            <Input
              value={name}
              maxLength={MAX_NAME_LENGTH}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") void submitRename();
              }}
            />
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              disabled={busy}
              onClick={() => setRenameOpen(false)}
            >
              <span className="normal-case tracking-normal">Cancel</span>
            </Button>
            <Button
              type="button"
              variant="gradient"
              size="sm"
              data-font="primary"
              disabled={busy || !name.trim() || name.trim() === (workspace.name ?? "").trim()}
              onClick={() => void submitRename()}
            >
              {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span className="normal-case tracking-normal">Save</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteOpen}
        onOpenChange={(open) => {
          setDeleteOpen(open);
          if (!open) setDeleteConfirmText("");
        }}
      >
        <DialogContent className={workspaceCardDialogClassName}>
          <DialogHeader>
            <DialogTitle className="text-[color:var(--brand-glacial-red)]">
              Delete workspace
            </DialogTitle>
            <DialogDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
              This permanently removes the canvas graph for this workspace. Linked strategies,
              reports, and simulations are not deleted.
            </DialogDescription>
          </DialogHeader>
          <Field className="gap-2">
            <div
              data-workspace-delete-confirm-prompt=""
              className="flex min-w-0 items-center gap-2 rounded-[6px] border border-border bg-muted/30 px-3 py-2"
            >
              <p className="min-w-0 flex-1 text-sm text-gray-500 dark:text-sidebar-muted-foreground">
                Type{" "}
                <span
                  data-workspace-delete-confirm-name=""
                  className="font-medium text-foreground"
                >
                  {workspaceDisplayName}
                </span>{" "}
                to confirm
              </p>
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                data-font="primary"
                className="shrink-0 text-muted-foreground hover:text-foreground"
                disabled={busy}
                aria-label="Copy workspace name"
                onClick={() => void copyWorkspaceName()}
              >
                <Copy className="size-4" aria-hidden />
              </Button>
            </div>
            <Input
              id="workspace-delete-confirm-input"
              value={deleteConfirmText}
              onChange={(event) => setDeleteConfirmText(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Enter" && deleteConfirmMatches && !busy) {
                  void submitDelete();
                }
              }}
              autoComplete="off"
              placeholder="Enter workspace name"
              disabled={busy}
            />
          </Field>
          <DialogFooter>
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              disabled={busy}
              onClick={() => setDeleteOpen(false)}
            >
              <span className="normal-case tracking-normal">Cancel</span>
            </Button>
            <Button
              type="button"
              variant="outline-red"
              size="sm"
              data-font="primary"
              disabled={busy || !deleteConfirmMatches}
              onClick={() => void submitDelete()}
            >
              {pending ? <Loader2 className="size-4 animate-spin" aria-hidden /> : null}
              <span className="normal-case tracking-normal">Delete workspace</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
